const STYLE_ID = "ls-dock-strip-style";
const HOST_CLASS = "ls-dock-host";

export const DOCK_SIDES = ["top", "bottom", "right"];

const STRIP_IDS = {
    top: "ls-dock-strip",
    bottom: "ls-dock-strip-bottom",
    right: "ls-dock-strip-right",
};

const MIN_ANCHORED_HEIGHT = 90;
const WORKSPACE_RESERVE = 180;
const WORKSPACE_RESERVE_WIDTH = 360;
const MIN_SIDE_WIDTH = 240;
const DEFAULT_SIDE_WIDTH = 340;
// How close to an edge a dragged window has to be to offer docking there
const ZONE_SIZE = 90;
const SIDE_ZONE_SIZE = 120;

const STYLE = `
    /* With docked windows, the tabs column becomes a grid so the side strip
       can sit next to the workspace and the others above and below it. */
    .${HOST_CLASS} {
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) auto;
        grid-template-rows: auto auto minmax(0, 1fr) auto;
    }
    .${HOST_CLASS} > [role="tablist"] {
        grid-row: 1;
        grid-column: 1;
    }
    .${HOST_CLASS} > [role="tabpanel"] {
        grid-row: 3;
        grid-column: 1;
        min-width: 0;
        min-height: 0;
    }
    .${HOST_CLASS} > .ls-dock-strip-top {
        grid-row: 2;
        grid-column: 1;
    }
    .${HOST_CLASS} > .ls-dock-strip-bottom {
        grid-row: 4;
        grid-column: 1;
    }
    .${HOST_CLASS} > .ls-dock-strip-right {
        grid-row: 2 / 5;
        grid-column: 2;
    }
    .ls-dock-strip {
        position: relative;
        display: flex;
        gap: 8px;
        box-sizing: border-box;
        min-width: 0;
        min-height: 0;
        background: #f8f6fc;
    }
    .ls-dock-strip-top,
    .ls-dock-strip-bottom {
        width: 100%;
        padding: 8px 12px;
        overflow-x: auto;
    }
    .ls-dock-strip-top {
        align-items: flex-start;
        border-bottom: 1px solid rgba(133, 92, 214, 0.18);
    }
    .ls-dock-strip-bottom {
        align-items: flex-end;
        border-top: 1px solid rgba(133, 92, 214, 0.18);
    }
    .ls-dock-strip-right {
        flex-direction: column;
        width: var(--ls-dock-side-width, ${DEFAULT_SIDE_WIDTH}px);
        padding: 8px;
        overflow-y: auto;
        border-left: 1px solid rgba(133, 92, 214, 0.18);
    }
    .ls-dock-strip:not(:has(.ls-dock.is-open)) {
        display: none;
    }
`;

const ensureStyles = () => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = STYLE;
    document.head.appendChild(style);
};

const getTabList = () => document.querySelector('[class*="gui_tab-list"]');
const getTabs = () => getTabList()?.closest('[class^="gui_tabs"]');

const getStrip = (side) => document.getElementById(STRIP_IDS[side]);

// Only strips showing an open window take up room
const visibleSize = (side, dimension) => {
    const strip = getStrip(side);
    if (!strip?.querySelector(".ls-dock.is-open")) return 0;
    return strip.getBoundingClientRect()[dimension];
};

let resizeScheduled = false;
export const notifyDockResize = () => {
    if (resizeScheduled) return;
    resizeScheduled = true;
    requestAnimationFrame(() => {
        resizeScheduled = false;
        window.dispatchEvent(new Event("resize"));
    });
};

// The blocks workspace only measures itself on window resize, but a strip
// also changes size with what its windows show (cameras turning on, tiles
// being resized...), which takes room from the workspace all the same.
const stripObserver = new ResizeObserver(() => notifyDockResize());

// The top and bottom strips share the column with the workspace, so each
// one can only grow into what the other leaves.
export const maxAnchoredHeight = (side = "top") => {
    const available =
        getTabs()?.getBoundingClientRect().height || window.innerHeight;
    const other = side === "top" ? "bottom" : "top";
    return Math.max(
        MIN_ANCHORED_HEIGHT,
        available - WORKSPACE_RESERVE - visibleSize(other, "height"),
    );
};

export const maxSideWidth = () => {
    const available =
        getTabs()?.getBoundingClientRect().width || window.innerWidth;
    return Math.max(MIN_SIDE_WIDTH, available - WORKSPACE_RESERVE_WIDTH);
};

export const getSideWidth = () => {
    const strip = getStrip("right");
    const value = strip?.style.getPropertyValue("--ls-dock-side-width");
    return parseFloat(value) || DEFAULT_SIDE_WIDTH;
};

export const setSideWidth = (width) => {
    const strip = getStrip("right");
    if (!strip) return;

    const clamped = Math.min(maxSideWidth(), Math.max(MIN_SIDE_WIDTH, width));
    strip.style.setProperty("--ls-dock-side-width", `${clamped}px`);
};

const ensureStrip = (side) => {
    let strip = getStrip(side);
    if (strip) return strip;

    const tabList = getTabList();
    const tabs = getTabs();
    if (!tabList || !tabs) return null;

    ensureStyles();
    strip = document.createElement("div");
    strip.id = STRIP_IDS[side];
    strip.className = `ls-dock-strip ls-dock-strip-${side}`;

    if (side === "top") tabList.insertAdjacentElement("afterend", strip);
    else tabs.appendChild(strip);

    tabs.classList.add(HOST_CLASS);
    stripObserver.observe(strip);
    return strip;
};

export const getDockZones = () => {
    const tabList = getTabList();
    const tabs = getTabs();
    if (!tabs || !tabList) return null;

    const tabsRect = tabs.getBoundingClientRect();
    const top = tabList.getBoundingClientRect().bottom;
    const sideLeft =
        tabsRect.right - Math.max(visibleSize("right", "width"), SIDE_ZONE_SIZE);

    return {
        top: {
            left: tabsRect.left,
            right: sideLeft,
            top,
            bottom: top + Math.max(visibleSize("top", "height"), ZONE_SIZE),
        },
        bottom: {
            left: tabsRect.left,
            right: sideLeft,
            top:
                tabsRect.bottom -
                Math.max(visibleSize("bottom", "height"), ZONE_SIZE),
            bottom: tabsRect.bottom,
        },
        right: {
            left: sideLeft,
            right: tabsRect.right,
            top,
            bottom: tabsRect.bottom,
        },
    };
};

export const findDockSide = (x, y) => {
    const zones = getDockZones();
    if (!zones) return null;

    return (
        DOCK_SIDES.find((side) => {
            const zone = zones[side];
            return (
                x >= zone.left &&
                x <= zone.right &&
                y >= zone.top &&
                y <= zone.bottom
            );
        }) || null
    );
};

export const attachToDockStrip = (panelEl, side = "top") => {
    const strip = ensureStrip(side);
    if (!strip) return false;

    strip.appendChild(panelEl);
    notifyDockResize();
    return true;
};

export const detachFromDockStrip = (panelEl) => {
    document.body.appendChild(panelEl);

    DOCK_SIDES.forEach((side) => {
        const strip = getStrip(side);
        if (strip && !strip.querySelector(".ls-dock")) {
            stripObserver.unobserve(strip);
            strip.remove();
        }
    });

    if (!DOCK_SIDES.some(getStrip)) getTabs()?.classList.remove(HOST_CLASS);

    notifyDockResize();
};
